import { useState } from 'react'
import { ArrowUpRight, Plus, Star, Trash2 } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Note } from '@/shared/ui/states'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { listUnits } from '@/features/geography/api'
import { UnitPath } from '@/features/geography/UnitPath'
import { explain } from '@/shared/api/problem'
import type { MobilizerDetailResponse } from '@/shared/api/types'
import { addAssignment, changeSupervisor, makeAssignmentPrimary, removeAssignment } from './api'
import { ROLES, RoleBadge } from './vocab'
import { SupervisorPicker } from './SupervisorPicker'

/**
 * Units and reporting line.
 *
 * ## Multi-unit is the normal case
 *
 * A county coordinator covers their county *and* helps in one of its wards.
 * That is two rows and both are legal — the rule the server enforces is one
 * bound, not two: a role may be assigned at its own level or anywhere below it,
 * never above. A ward captain assigned to a county is a promotion by data entry.
 * The hint under the picker names the shallowest unit this person's role allows,
 * so the refusal is predictable rather than a surprise 400.
 *
 * ## Why the primary cannot simply be deleted
 *
 * `DELETE` on the primary assignment is refused. The alternatives are worse:
 * picking somebody's new home unit for them is a decision the server is not
 * entitled to make, and leaving the profile pointing at a deleted row defeats
 * the constraint that makes "exactly one primary" true. Promote another first —
 * which is one click, right there in the list.
 */
export function AssignmentsPanel({
  detail, canAssign, onChanged, onOpenOther,
}: {
  detail: MobilizerDetailResponse
  canAssign: boolean
  onChanged: () => void
  onOpenOther?: (id: string) => void
}) {
  const p = detail.profile
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const shallowest = ROLES[p.roleCode]?.shallowestUnit ?? 'WARD'

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null)
    try { await fn(); onChanged() } catch (err) { setError(explain(err)) } finally { setBusy(false) }
  }

  return (
    <div className="space-y-3">
      <ErrorStrip message={error} />

      <Panel
        title={`Units they work in (${detail.assignments.length})`}
        description="Overlapping units are normal. Exactly one is the home unit, and that is the one every scope query uses."
      >
        {detail.assignments.length === 0 ? (
          <EmptyState title="No units" hint="Everyone has at least one — this is a data problem worth reporting." />
        ) : (
          <ul className="divide-y divide-border">
            {detail.assignments.map((a) => (
              <li key={a.id} className="py-2 flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-erp-sm font-medium truncate">
                    {a.name ?? a.id}
                    {a.typeCode && (
                      <span className="ml-2 text-erp-xs text-muted-foreground font-normal">
                        {a.typeCode.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    )}
                  </span>
                  <UnitPath stationId={a.id} path={a.path} className="justify-start" />
                </span>
                {a.primary ? (
                  <span className="inline-flex items-center gap-1 text-erp-xs text-chocolate font-medium whitespace-nowrap">
                    <Star className="h-3.5 w-3.5" /> Home unit
                  </span>
                ) : canAssign && (
                  <span className="flex gap-1.5 shrink-0">
                    <button type="button" className="erp-btn" disabled={busy}
                            onClick={() => void act(() => makeAssignmentPrimary(p.id, a.id))}>
                      <Star className="h-3.5 w-3.5" /> Make home
                    </button>
                    <button type="button" className="erp-btn" disabled={busy}
                            aria-label={`Remove ${a.name ?? 'unit'}`}
                            onClick={() => void act(() => removeAssignment(p.id, a.id))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {canAssign && (
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <AsyncMultiSelect
              label="Add unit"
              selected={picked}
              onChange={setPicked}
              placeholder="Search counties, constituencies, wards, stations"
              search={async (term) => {
                const found = await listUnits({ q: term } as never, 0, 10)
                return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
              }}
            />
            <p className="text-erp-xs text-muted-foreground">
              A {ROLES[p.roleCode]?.label.toLowerCase() ?? 'mobilizer'} may be assigned at{' '}
              <strong>{shallowest.replace(/_/g, ' ').toLowerCase()}</strong> level or anywhere
              below it — never above. The server checks this on every assignment, not just the
              first, because a bulk import will not come through this screen.
            </p>
            <button
              type="button"
              className="erp-btn-primary"
              disabled={busy || picked.length === 0}
              onClick={() => void act(async () => {
                for (const u of picked) await addAssignment(p.id, u.value)
                setPicked([])
              })}
            >
              <Plus className="h-3.5 w-3.5" />
              Add {picked.length || ''} unit{picked.length === 1 ? '' : 's'}
            </button>
          </div>
        )}
      </Panel>

      <Panel
        title="Reporting line"
        description="Changing a supervisor moves this person and everyone under them. The confirm says how many that is before anything happens."
      >
        {detail.supervisorChain.length === 0 ? (
          <Note tone="info">
            Nobody above them. That is what a national director looks like, and it is also
            what somebody looks like after their own supervisor's engagement ended.
          </Note>
        ) : (
          <ol className="space-y-1.5">
            {detail.supervisorChain.map((s, i) => (
              <li key={s.id} className="flex items-center gap-2 text-erp-sm"
                  style={{ paddingLeft: `${i * 14}px` }}>
                <span className="text-muted-foreground text-erp-xs">{i === 0 ? 'Reports to' : '↳'}</span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-chocolate min-w-0"
                  onClick={() => onOpenOther?.(s.id)}
                >
                  <span className="truncate">{s.fullName}</span>
                  <ArrowUpRight className="h-3 w-3 shrink-0" />
                </button>
                <RoleBadge code={s.roleCode} />
              </li>
            ))}
          </ol>
        )}

        {canAssign && (
          <div className="mt-3 pt-3 border-t border-border">
            <SupervisorPicker
              mobilizerId={p.id}
              mobilizerName={p.fullName}
              roleCode={p.roleCode}
              currentSupervisorId={p.supervisorId ?? null}
              onChanged={onChanged}
              apply={(supervisorId) => changeSupervisor(p.id, supervisorId)}
            />
          </div>
        )}
      </Panel>
    </div>
  )
}
